import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useGroupSearch } from '../../../../api/groups'
import { getSuppression, setSuppression } from '../../../../utils/globalSuppression'
import { getInitials } from './getInitials'
import { ScopeDisplayItem } from './ScopeDisplayItem'
import ScopeSelect from './ScopeSelect'

/** Search for all the options */
const useGroupTypeOptions = (scope, labels) => {
  const scopeQuery = useMemo(() => {
    const groupTypeId = scope?.path ? scope.path.at(0) : 201
    return ({
      filters: {
        levelTypeId: [{
          value: groupTypeId,
          op: 'eq'
        }]
      },
      sort: [{ field: 'longName', dir: 'asc' }],
      take: 1000
    })
  }, [scope])

  const { data, isLoading } = useGroupSearch(scopeQuery)
  const { options, pinnedOptions } = useMemo(() => {
    const pinnedResult = []

    pinnedResult.push({
      label: labels?.allItems ?? 'All Clients',
      value: JSON.stringify({ type: 1001, id: null }),
      attributes: { type: 1001, id: null, abbreviation: 'All', subTitle: labels.subTitle }
    })

    const result = []
    const d = data?.data || []
    d.forEach(g => {
      result.push({
        label: g.longName,
        value: JSON.stringify({ type: g.levelTypeId, id: g.groupId }),
        attributes: {
          type: g.levelTypeId,
          id: g.groupId,
          avatarUrl: g.avatarUrl ?? g.profile?.profileData?.profilePic,
          abbreviation: getInitials(g.longName),
          subTitle: labels.subTitle
        }
      })
    })

    return {
      options: result,
      pinnedOptions: pinnedResult
    }
  }, [data, labels])

  return { options, pinnedOptions, isLoading }
}

const useOnChangeGroupScope = (scope, scopeValues, setScope) => {
  const onChangeGroupScope = useCallback((group) => {
    if (Array.isArray(group)) {
      setScope(group.map(g => g.attributes.id))
      return
    }

    const levelTypeId = group.attributes.type
    const groupId = group.attributes.id

    const scopeValues = levelTypeId === 1001 ? [] : [groupId]

    // setScope sets url state and such appropriately
    setScope(scopeValues)
  }, [setScope])

  const [currentValue, currentValueString] = useMemo(() => {
    const groupTypeId = (scope.path || []).at(0)

    const type = scopeValues?.length ? groupTypeId : 1001
    const scopeValue = { type, values: type === 1001 ? null : scopeValues }
    return [scopeValue, JSON.stringify(scopeValue)]
  }, [scope, scopeValues])

  return { onChangeGroupScope, currentValue, currentValueString }
}

// Search for group matching the specified scope
const useDisplayGroup = (currentScope, labels) => {
  const scopeQuery = useMemo(() => {
    if (!currentScope?.values) return null

    return {
      filters: {
        levelTypeId: [{
          value: currentScope.type,
          op: 'eq'
        }],
        groupId: [{
          op: 'in',
          value: currentScope.values
        }]
      },
      take: 1
    }
  }, [currentScope?.type, currentScope?.values])

  const { data, isFetching } = useGroupSearch(scopeQuery, { enabled: !!scopeQuery })
  const group = useMemo(() => {
    if (currentScope?.type === 1001 && currentScope?.values === null) {
      return {
        label: labels?.allItems ?? 'All Clients',
        abbreviation: 'All'
      }
    }
    if (currentScope.values.length > 1) {
      return {
        label: labels?.multipleItems ?? 'Multiple',
        abbreviation: 'M'
      }
    }
    const g = data?.data ? data.data.at(0) : null
    return g ? ({
      label: g.longName,
      avatarUrl: g?.avatarUrl ?? g.extras?.profileData?.profilePic,
      abbreviation: g.abbreviation || getInitials(g.longName),
      subTitle: labels?.subTitle
    }) : null
  }, [data, currentScope, labels])

  return {
    group,
    isGroupFetching: isFetching
  }
}

const GroupScopeDisplay = ({
  scopeValues,
  scope,
  setScope,
  isLoading,
  hideAvatars,
  size,
  disableReset,
  labels,
  multiSelect,
  className
}) => {
  const { onChangeGroupScope, currentValue } = useOnChangeGroupScope(scope, scopeValues, setScope)

  /* Here we need to take the raw value, and determine the system's representation of that level */
  const { group, isGroupFetching } = useDisplayGroup(currentValue, labels)

  /* If we've selected something we don't have, it is invalid - probably deleted - the provider stores selections in local storage maybe */
  const isValid = useMemo(() => {
    return isGroupFetching || (!isGroupFetching && !!group)
  }, [group, isGroupFetching])

  const { options, pinnedOptions, isFetching: optionsLoading } = useGroupTypeOptions(scope, labels)

  /**
   * If we've selected an invalid selection, then try to reset ONE TIME
   */
  useEffect(() => {
    if (disableReset) return

    const key = 'GroupScopeDisplay'
    const suppression = getSuppression(key)
    if (!suppression && !isValid && !isLoading && !optionsLoading) {
      setSuppression(key, true)
      onChangeGroupScope(pinnedOptions.at(0))
    } else if (!suppression && !isLoading && !optionsLoading) {
      setSuppression(key, true)
    }
  }, [isValid, onChangeGroupScope, pinnedOptions, isLoading, optionsLoading, scope, disableReset])

  return (
    <ScopeSelect
      options={options}
      pinnedOptions={pinnedOptions}
      isLoading={isLoading || isGroupFetching}
      onSelect={onChangeGroupScope}
      currentlySelectedValues={scopeValues}
      multiSelect={multiSelect}
      className={className}
    >
      {!isValid ? (
        <ScopeDisplayItem
          name='Select an Option'
          hideAvatar={hideAvatars}
          size={size}
        />
      ) : group ? (
        <ScopeDisplayItem
          name={group.label}
          subTitle={group.subTitle}
          avatarSrc={group?.avatarUrl}
          abbreviation={group?.abbreviation}
          hideAvatar={hideAvatars}
          size={size}
        />
      ) : null}
    </ScopeSelect>
  )
}

GroupScopeDisplay.propTypes = {
  /** The currently-selected filter values */
  scopeValues: PropTypes.arrayOf(PropTypes.number),
  scope: PropTypes.object,
  setScope: PropTypes.func,
  isLoading: PropTypes.bool,
  hideAvatars: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'lg']),
  disableReset: PropTypes.bool,
  labels: PropTypes.shape({
    subTitle: PropTypes.string,
    allItems: PropTypes.string,
    allMyItems: PropTypes.string,
    multipleItems: PropTypes.string
  }),
  multiSelect: PropTypes.bool,
  className: PropTypes.string
}

GroupScopeDisplay.defaultProps = {
  labels: {
    subTitle: '',
    allItems: 'All Clients',
    allMyItems: 'My Clients',
    multipleItems: 'Multiple'
  },
  multiSelect: false
}

export default GroupScopeDisplay
